import {
    Timeline,
    TimelineConnector,
    TimelineContent,
    TimelineDot,
    TimelineItem,
    TimelineOppositeContent,
    timelineOppositeContentClasses,
    TimelineSeparator
} from '@mui/lab'
import {Box, Button, Grid, Stack, Typography, useTheme} from "@mui/material";
import {Firma, FirmaFields, FirmaLabel, FirmaPutFields} from "../../types/firma";
import {makeStyles} from '@mui/styles';
import {useState} from "react";
import {useForm} from "react-hook-form";
import toast from "react-hot-toast";
import {useNavigate} from "react-router-dom";
import {ControlledTextField} from "src/components/forms";
import {BaseResponseFields, EntityWithIdFields} from "src/types";
import {dateFormatter} from "src/utils/formatters";
import {userStorage} from "src/utils/localStorage";
import {z} from "zod";
import {HttpSistema} from "../../api/httpSistema";
import Icon from "src/@core/components/icon";
import CustomChip from 'src/@core/components/mui/chip'
import {FirmasProps} from "../SeccionFirmas";
import {SistemaTipo} from "../../../../types/enum/enum";
import {Theme} from "@mui/material/styles";


const ICON_FIRMA = <Icon icon={'noto:fountain-pen'} style={{fontSize: '1.2em'}}/>

const useStyles = makeStyles({
    flexStartCentered: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        width: '100%'
    },
    flexSpaceBetweenCentered: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%'
    },
    text: {
        display: '-webkit-box',
        boxOrient: 'vertical',
        lineClamp: 2,
        wordBreak: 'break-all',
        overflow: 'hidden'
    }
})

const getFirmaColor = (codFirmaResultado: number | null, theme: Theme) => {
    return !codFirmaResultado ? 'primary' : (codFirmaResultado === 1) ? 'success' : (codFirmaResultado === 2 ? 'error' : 'primary')
}

type WrapperFirmaProps = {
    children: React.ReactNode
    cargo: string
    estado: number | null, //'firmar' | 'aprobado' | 'rechazado' | 'pendiente'
}

const sistemasFirmaSinObservaciones: [SistemaTipo] = [SistemaTipo.CapitalMarkets]

const WrapperFirma = ({children, cargo, estado}: WrapperFirmaProps) => {

    const classes = useStyles();
    const theme = useTheme()

    const getIconBgColor = () => {
        if (!estado) {
            return theme.palette.customColors.avatarBg;
        }
        if (estado === 1) { // Aprobado
            return theme.palette.success.light;
        }
        if (estado === 2) { // Rechazado
            return theme.palette.error.light;
        }

        return theme.palette.primary.light;
    }

    return (
        <TimelineItem>
            <TimelineOppositeContent display='flex' alignItems='center'  justifyContent='flex-start'>
                <CustomChip size='small' color='info' label={cargo}/>
            </TimelineOppositeContent>
            <TimelineSeparator>
                <TimelineConnector/>
                <TimelineDot
                    sx={{bgcolor: getIconBgColor()}}
                >
                    {ICON_FIRMA}
                </TimelineDot>
                <TimelineConnector/>
            </TimelineSeparator>
            <TimelineContent sx={{py: 4}}>
                <Stack spacing={4} width='100%'>
                    {children}
                </Stack>
            </TimelineContent>
        </TimelineItem>
    )
}

const schemaActualizarFirma = z.object({
    observaciones: z.string().optional(),   // TODO: maxlength
    // codFirmaResultado: z.number(),
    mailUsuario: z.string().email()
})

type SchemaActualizarFirma = z.infer<typeof schemaActualizarFirma>

type FirmaParaFirmarProps = {
    idOperacion: number,
    codSistema: number,
    firma: Firma,
    reload: () => void,
    labels: FirmaLabel[]
}

const FirmaFirmar = ({idOperacion, codSistema, firma, reload, labels}: FirmaParaFirmarProps) => {

    // Id del label que se clickeó para firmar, para deshabilitar los otros botones.
    const [firmando, setFirmando] = useState<number>(0);

    const {codFirmaResultado, etiqueta, cargo, observaciones, apellidoYNombre, mailUsuarioFirma, fechaFirma} = firma;

    const user = userStorage.get()

    const formMethods = useForm<SchemaActualizarFirma>({
        defaultValues: {
            observaciones: observaciones ?? ''
        }
    })

    const navigation = useNavigate()

    const onSubmitWrapper = (type: number) => {
        return new Promise<void>((resolve, reject) => {
            formMethods.handleSubmit(
                (formData) => {
                    toast.promise(handleFirmar(formData, type), {
                        loading: 'Firmando',
                        success: type === 1 ? 'Aprobado correctamente' :
                            type === 2 ? 'Rechazado correctamente' : 'Firmado correctamente.',
                        error: (e) => `Error: ${e}`
                    })
                        .then(() => {
                            setTimeout(() => navigation('/'), 500)
                            resolve()
                        })
                        .catch((err) => {
                            reject(err)
                            setFirmando(0)
                        })
                },
                (validationErrors) => {
                    reject(validationErrors)
                    console.error(validationErrors)
                    setFirmando(0)
                }
            )();
        })
    }

    const handleFirmar = (data: SchemaActualizarFirma, type: number) => {
        const firmaUpdate = {
            [FirmaPutFields.CodFirmaResultado]: type,
            [FirmaPutFields.CodUsuarioFirma]: user.userId,
            [FirmaPutFields.FirmaUsuario]: '',
            [FirmaPutFields.MailUsuario]: user.mail,
            [FirmaPutFields.Observaciones]: data.observaciones
        }

        return new Promise<void>((resolve, reject) => {
            HttpSistema.putFirma(idOperacion, codSistema, firma[EntityWithIdFields.Id], firmaUpdate)
                .then((res) => {
                    if (res?.[BaseResponseFields.HasError]) {
                        reject(res?.[BaseResponseFields.ErrorDescription] || '')
                    }
                    resolve(res)
                })
                .catch((err) => {
                    reject(err?.[BaseResponseFields.ErrorDescription] || '')
                })
        })
    }

    const theme = useTheme()
    const classes = useStyles();

    {/*=== 1 ? 'aprobado' : codFirmaResultado === 2 ? 'rechazado' : 'firmar'}*/
    }

    return (
        <WrapperFirma
            cargo={cargo ?? ''}
            estado={codFirmaResultado}
        >
      {
          codFirmaResultado !== 0 &&
          <Stack className={classes.flexSpaceBetweenCentered} direction='row' spacing={2}>
              <Box>
                  <Typography sx={{fontWeight: 600}}>
                      {codFirmaResultado === 0 ? 'Pendiente de firma' : (apellidoYNombre || '')}
                  </Typography>
                  <Typography variant='subtitle2'>
                      {codFirmaResultado === 0 ? 'Usted no tiene permisos para firmar.' : (mailUsuarioFirma || '')}
                  </Typography>
              </Box>
              <Stack spacing={1}>
                  {
                      codFirmaResultado == 0 ? <CustomChip label='Pendiente' size='small' skin='light'/> :
                          codFirmaResultado == 1 ?
                              <CustomChip label='Aprobado' color='success' size='small' skin='light'/> :
                              codFirmaResultado == 2 ?
                                  <CustomChip label='Rechazado' color='error' size='small' skin='light'/> : <></>
                  }
                  {
                      fechaFirma && <Typography variant='caption'>{dateFormatter.toShortDate(fechaFirma)}</Typography>
                  }
              </Stack>
          </Stack>
      }
          {
              !sistemasFirmaSinObservaciones.includes(codSistema) &&
              <ControlledTextField
                  label='Observaciones'
                  name='observaciones'
                  multiline
                  rows={3}
                  fullWidth
                  control={formMethods.control}
              />
          }

          <Stack direction='row-reverse' display='flex' justifyContent='right' spacing={2}>
              {
                  labels.map(label => (
                      <Button
                          key={label.id}
                          variant='contained'
                          color={
                              label.id === 1 ? 'success' : label.id === 2 ? 'error' : 'primary'
                          }
                          onClick={() => {
                              setFirmando(label.id)

                              return onSubmitWrapper(label.id)
                          }}
                          disabled={(firmando !== 0)}

                          // disabled={(firmando === 0) ? false : (firmando !== label.id)}    // Puede servir si hay que bloquear un botón y el clickeado no.
                      >
                          {label.firmaResultadoDescripcion}
                      </Button>
                  ))
              }
          </Stack>
        </WrapperFirma>
    )
}

const FirmaFija = ({firma}: { firma: Firma }) => {

    const {
        fechaFirma,
        mailUsuarioFirma,
        apellidoYNombre,
        observaciones,
        etiqueta,
        cargo,
        codFirmaResultado,
        descResultadoFirmaCompletada
    } = firma;

    const theme = useTheme()
    const classes = useStyles();

    // para mantener compatibilidad con los sistemas que no tienen implementado el descResultadoFirmaCompletada
    let firmaLabel: string;
    if (descResultadoFirmaCompletada) {
        firmaLabel = descResultadoFirmaCompletada;
    }

    else {
        switch (codFirmaResultado) {
            case 0:
                firmaLabel = 'Pendiente';
                break;
            case 1:
                firmaLabel = 'Aprobado';
                break;
            case 2:
                firmaLabel = 'Rechazado';
                break;
            case 4:
                firmaLabel = 'Propuesto';
                break;
            case 6:
                firmaLabel = 'Consultado';
                break;
            default:
                firmaLabel = 'Pendiente';
        }
    }

    const firmaColor = getFirmaColor(codFirmaResultado, theme);

    return (
        <WrapperFirma
            cargo={cargo ?? ''}
            estado={codFirmaResultado}
        >
            <Stack className={classes.flexSpaceBetweenCentered} direction='row' spacing={2}>
                <Box>
                    <Typography sx={{fontWeight: 600}}>
                        {codFirmaResultado === 0 ? 'Pendiente de firma' : (apellidoYNombre || '')}
                    </Typography>
                    <Typography variant='subtitle2'>
                        {codFirmaResultado === 0 ? 'Usted no tiene permisos para firmar.' : (mailUsuarioFirma || '')}
                    </Typography>
                </Box>
                <Stack spacing={1}>
                    <CustomChip label={firmaLabel} size='small' color={firmaColor} skin='light'/>
                    {
                        fechaFirma &&
                        <Typography align='right' variant='caption'>{dateFormatter.toShortDate(fechaFirma)}</Typography>
                    }
                </Stack>
            </Stack>
            {
                observaciones && <Typography variant='body1'>{observaciones}</Typography>
            }
        </WrapperFirma>
    )
}

export const FirmasDesktop = ({firmas, idOperacion, codSistema, refetch}: FirmasProps) => {

    return (
        <Timeline
            sx={{
                [`& .${timelineOppositeContentClasses.root}`]: {
                    flex: 0.25,
                },
            }}
        >
            {
                firmas && firmas.firmas.map(firma => {
                    const labels = firmas.labels.filter(label => label.intIdOperacionFirma === firma.id)


                    let componente;

                    if (firma[FirmaFields.PermiteFirmar]) {
                        componente = <FirmaFirmar
                            idOperacion={idOperacion}
                            firma={firma}
                            reload={refetch}
                            labels={labels}
                            codSistema={codSistema}
                        />
                    } else {
                        componente = <FirmaFija firma={firma}/>
                    }

                    return (
                        <Grid item key={firma.id} xs={12}>
                            {componente}
                        </Grid>
                    )
                })
            }
        </Timeline>
    )
}
